"use strict";

import ApiService from './api/ApiService';
import MigrationActions from '../actions/MigrationActions';
import AuthStore from '../stores/AuthStore';
import {MigrationStatusValue} from './migrationUtils';

const PHASE1_INTERVAL_MS = 6000; // 6 seconds
const PHASE1_MAX_CHECKS = 15; // 15 checks x 6s = 90 seconds (1.5 minutes)
const PHASE2_INTERVAL_MS = 30000; // 30 seconds
const PHASE2_MAX_CHECKS = 40; // 40 checks x 30s = 20 minutes
const MAX_TOTAL_MS = (PHASE1_MAX_CHECKS * PHASE1_INTERVAL_MS)
    + (PHASE2_MAX_CHECKS * PHASE2_INTERVAL_MS); // 1.5min + 20min = 21.5 minutes (hard cap across both phases)

/** @type {?{ userName: string, aborted: boolean, timeoutId: ?number }} */
let activePoll = null;

/**
 * localStorage key for an in-flight migration poll for the signed-in user.
 *
 * @param {string} userName
 * @return {?string}
 */
export function getMigrationPendingStorageKey(userName) {
    if (!userName) {
        return null;
    }
    return `${userName}_pending`;
}

/**
 * @param {string} userName
 */
export function setMigrationPendingFlag(userName) {
    const key = getMigrationPendingStorageKey(userName);
    if (key) {
        localStorage.setItem(key, '1');
    }
}

/**
 * @param {string} userName
 */
export function clearMigrationPendingFlag(userName) {
    const key = getMigrationPendingStorageKey(userName);
    if (key) {
        localStorage.removeItem(key);
    }
}

/**
 * @param {string} userName
 * @return {boolean}
 */
export function hasMigrationPendingFlag(userName) {
    const key = getMigrationPendingStorageKey(userName);
    return !!(key && localStorage.getItem(key) != null);
}

/**
 * Resolve the username used for migration polling storage keys.
 *
 * @param {?Object} migrationStatusRecord MigrationStore migrationStatus record
 * @return {?string}
 */
export function resolveMigrationUserName(migrationStatusRecord) {
    if (migrationStatusRecord && migrationStatusRecord.userName) {
        return migrationStatusRecord.userName;
    }
    if (AuthStore.identity && AuthStore.identity.username) {
        return AuthStore.identity.username;
    }
    return null;
}

/**
 * Stop any in-flight migration status polling and pending timers.
 */
export function stopMigrationPolling() {
    if (!activePoll) {
        return;
    }
    activePoll.aborted = true;
    if (activePoll.timeoutId != null) {
        clearTimeout(activePoll.timeoutId);
        activePoll.timeoutId = null;
    }
    activePoll = null;
}

/**
 * @param {number} ms
 * @param {{ aborted: boolean, timeoutId: ?number }} pollState
 * @return {Promise<void>}
 */
function delay(ms, pollState) {
    return new Promise((resolve) => {
        pollState.timeoutId = setTimeout(() => {
            pollState.timeoutId = null;
            resolve();
        }, ms);
    });
}

/**
 * @param {?string} status
 * @return {boolean}
 */
function isPendingStatus(status) {
    return status === MigrationStatusValue.PENDING;
}

/**
 * Fetch migration status, update the store without toggling the page loading state.
 *
 * @return {Promise<?string>}
 */
async function fetchCurrentMigrationStatus() {
    const migrationStatusDTO = await ApiService.getMigrationStatus();
    MigrationActions.applyPolledMigrationStatus(migrationStatusDTO);
    return migrationStatusDTO && migrationStatusDTO.MigrationStatus;
}

/**
 * Run one status check. Returns false when polling should stop.
 *
 * @param {{ userName: string, aborted: boolean, timeoutId: ?number }} pollState
 * @return {Promise<boolean>}
 */
async function runStatusCheck(pollState) {
    if (pollState.aborted) {
        return false;
    }

    let status;
    try {
        status = await fetchCurrentMigrationStatus();
    } catch (error) {
        // Keep polling on transient failures; the overall 15-minute cap still applies.
        return true;
    }

    if (!isPendingStatus(status)) {
        clearMigrationPendingFlag(pollState.userName);
        pollState.aborted = true;
        return false;
    }

    return true;
}

/**
 * @param {{ userName: string, aborted: boolean, timeoutId: ?number }} pollState
 * @param {number} intervalMs
 * @param {number} maxChecks
 * @param {number} deadlineMs
 * @return {Promise<void>}
 */
async function runPollingPhase(pollState, intervalMs, maxChecks, deadlineMs) {
    for (let checkIndex = 0; checkIndex < maxChecks; checkIndex += 1) {
        if (pollState.aborted || Date.now() >= deadlineMs) {
            return;
        }

        const shouldContinue = await runStatusCheck(pollState);
        if (!shouldContinue) {
            return;
        }

        const isLastCheckInPhase = checkIndex === maxChecks - 1;
        if (isLastCheckInPhase || pollState.aborted || Date.now() >= deadlineMs) {
            return;
        }

        await delay(intervalMs, pollState);
        if (pollState.aborted) {
            return;
        }
    }
}

/**
 * Poll migration status on the two-phase schedule until completion, timeout, or max checks.
 *
 * @param {string} userName
 * @return {Promise<void>}
 */
export async function startMigrationPolling(userName) {
    if (!userName) {
        return;
    }

    stopMigrationPolling();

    const pollState = {
        userName,
        aborted: false,
        timeoutId: null
    };
    activePoll = pollState;
    setMigrationPendingFlag(userName);

    const deadlineMs = Date.now() + MAX_TOTAL_MS;

    try {
        await runPollingPhase(pollState, PHASE1_INTERVAL_MS, PHASE1_MAX_CHECKS, deadlineMs);
        if (!pollState.aborted && Date.now() < deadlineMs) {
            await runPollingPhase(pollState, PHASE2_INTERVAL_MS, PHASE2_MAX_CHECKS, deadlineMs);
        }
    } finally {
        if (!pollState.aborted) {
            clearMigrationPendingFlag(userName);
            pollState.aborted = true;
        }
        if (activePoll === pollState) {
            if (pollState.timeoutId != null) {
                clearTimeout(pollState.timeoutId);
            }
            activePoll = null;
        }
    }
}

/**
 * Ensure polling runs whenever migration is PENDING; stop when it is not.
 *
 * @param {?Object} migrationStatusRecord MigrationStore migrationStatus record
 */
export function syncMigrationPolling(migrationStatusRecord) {
    if (!AuthStore.isSignedIn()) {
        stopMigrationPolling();
        return;
    }

    const userName = resolveMigrationUserName(migrationStatusRecord);
    const status = migrationStatusRecord && migrationStatusRecord.migrationStatus;
    const isPending = isPendingStatus(status);

    if (!isPending) {
        if (userName) {
            clearMigrationPendingFlag(userName);
        }
        stopMigrationPolling();
        return;
    }

    if (!userName) {
        return;
    }

    setMigrationPendingFlag(userName);

    const isPollingForUser = activePoll
        && activePoll.userName === userName
        && !activePoll.aborted;

    if (!isPollingForUser) {
        startMigrationPolling(userName);
    }
}
